import React from 'react';
import { cn } from '../../../lib/utils';
import { getSkillIcon } from '@/lib/skills';
import {
  BarChart3,
  Building,
  Database,
  Shield,
  Cloud,
  Zap,
  Factory,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
  Brain,
  Shuffle,
  Settings,
  GitBranch,
  TrendingUp,
  FileText,
  Bot,
  Network,
  ArrowLeftRight,
  Wrench,
  Terminal,
  Plug,
  CheckCircle,
  Briefcase,
  Crown,
  Users,
  RotateCcw,
  GraduationCap,
  UserCheck,
  Target,
  Palette,
  Code,
  Cpu,
  Sparkles,
  Server
} from 'lucide-react';

// Mapping from icon identifiers to actual Lucide React components
const LUCIDE_ICON_MAP: Record<string, React.ComponentType<any>> = {
  'lucide:BarChart3': BarChart3,
  'lucide:Building': Building,
  'lucide:Database': Database,
  'lucide:Shield': Shield,
  'lucide:Cloud': Cloud,
  'lucide:Zap': Zap,
  'lucide:Factory': Factory,
  'lucide:ShieldCheck': ShieldCheck,
  'lucide:ShieldAlert': ShieldAlert,
  'lucide:AlertTriangle': AlertTriangle,
  'lucide:ClipboardCheck': ClipboardCheck,
  'lucide:MessageSquare': MessageSquare,
  'lucide:Brain': Brain,
  'lucide:Shuffle': Shuffle,
  'lucide:Settings': Settings,
  'lucide:GitBranch': GitBranch,
  'lucide:TrendingUp': TrendingUp,
  'lucide:FileText': FileText,
  'lucide:Bot': Bot,
  'lucide:Network': Network,
  'lucide:ArrowLeftRight': ArrowLeftRight,
  'lucide:Wrench': Wrench,
  'lucide:Terminal': Terminal,
  'lucide:Plug': Plug,
  'lucide:CheckCircle': CheckCircle,
  'lucide:Briefcase': Briefcase,
  'lucide:Crown': Crown,
  'lucide:Users': Users,
  'lucide:RotateCcw': RotateCcw,
  'lucide:GraduationCap': GraduationCap,
  'lucide:UserCheck': UserCheck,
  'lucide:Target': Target,
  'lucide:Palette': Palette,
  'lucide:Code': Code,
  'lucide:Cpu': Cpu,
  'lucide:Sparkles': Sparkles,
  'lucide:Server': Server,
};

interface SkillIconProps {
  skillName: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showFallback?: boolean;
}

interface SkillIconListProps {
  skills: string[];
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showFallback?: boolean;
  maxIcons?: number;
}

interface SkillBadgeProps extends SkillIconProps {
  label?: string;
  variant?: 'default' | 'secondary' | 'outline';
  level?: number;
  onClick?: () => void;
}

const sizeClasses = {
  'xs': 'text-xs',
  'sm': 'text-sm', 
  'md': 'text-base',
  'lg': 'text-lg',
  'xl': 'text-xl',
  '2xl': 'text-2xl'
};

// Size classes for custom images only
const imageSizeClasses = {
  'xs': 'w-3 h-3',
  'sm': 'w-4 h-4',
  'md': 'w-5 h-5', 
  'lg': 'w-6 h-6',
  'xl': 'w-8 h-8',
  '2xl': 'w-10 h-10'
};

const BADGE_SIZE_CLASSES = {
  xs: 'px-1.5 py-0.5 text-xs gap-1',
  sm: 'px-2 py-1 text-xs gap-1',
  md: 'px-3 py-1.5 text-sm gap-1.5',
  lg: 'px-4 py-2 text-base gap-2',
  xl: 'px-5 py-2.5 text-lg gap-2.5',
  '2xl': 'px-6 py-3 text-xl gap-3'
} as const;

/**
 * Renders an icon for a programming language, framework, or tool
 * Supports both devicon classes and custom PNG images
 */
export const SkillIcon: React.FC<SkillIconProps> = ({ 
  skillName, 
  size = 'md', 
  className = '', 
  showFallback = false // Changed default to false to remove dummy icons
}) => {
  const iconClass = getSkillIcon(skillName);
  const fontSizeClass = sizeClasses[size];
  const imageSizeClass = imageSizeClasses[size];
  
  // Check if it's a Lucide React icon (starts with lucide:)
  if (iconClass && iconClass.startsWith('lucide:')) {
    const IconComponent = LUCIDE_ICON_MAP[iconClass];
    if (IconComponent) {
      return (
        <IconComponent 
          className={cn(imageSizeClass, className)}
          title={`${skillName} icon`}
        />
      );
    }
  }
  
  // Check if it's a custom image (starts with /)
  if (iconClass && iconClass.startsWith('/')) {
    return (
      <img 
        src={iconClass} 
        alt={`${skillName} icon`}
        className={cn(imageSizeClass, className)}
        style={{ objectFit: 'contain' }}
        onError={(e) => {
          // Just hide the image if it fails to load, no fallback
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }
  
  // For devicon classes
  if (iconClass) {
    return (
      <i 
        className={cn(iconClass, fontSizeClass, className)}
        title={`${skillName} icon`}
      />
    );
  }
  
  // Only show fallback if explicitly requested (which is now false by default)
  if (showFallback) {
    return (
      <span 
        className={cn(
          'inline-flex items-center justify-center rounded-md bg-gray-100 text-gray-600 font-semibold',
          imageSizeClass,
          fontSizeClass,
          className
        )}
        title={skillName}
      >
        {skillName.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  
  // Return null if no icon found and no fallback requested
  return null;
};

/**
 * Renders a list of skill icons in a horizontal layout
 */
export const SkillIconList: React.FC<SkillIconListProps> = ({ 
  skills, 
  size = 'md', 
  className = '', 
  showFallback = false, // Changed to false by default
  maxIcons
}) => {
  const displaySkills = maxIcons ? skills.slice(0, maxIcons) : skills;
  const remainingCount = maxIcons && skills.length > maxIcons ? skills.length - maxIcons : 0;
  
  return (
    <div className={cn('flex flex-wrap gap-2 items-center', className)}>
      {displaySkills.map((skill, index) => (
        <SkillIcon 
          key={`${skill}-${index}`}
          skillName={skill} 
          size={size} 
          showFallback={showFallback}
        />
      ))}
      {remainingCount > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

/**
 * Renders a skill icon with an optional label in a badge-like format
 */
export const SkillBadge: React.FC<SkillBadgeProps> = ({ 
  skillName, 
  label, 
  size = 'sm', 
  className = '', 
  variant = 'default',
  showFallback = false,
  level,
  onClick 
}) => {
  const variantClasses = {
    'default': 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700',
    'secondary': 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
    'outline': 'bg-transparent text-gray-700 border-gray-300 dark:text-gray-200 dark:border-gray-600'
  };
  
  const badgeSizeClass = BADGE_SIZE_CLASSES[size];
  
  const badgeContent = (
    <>
      <SkillIcon 
        skillName={skillName} 
        size={size} 
        showFallback={showFallback}
      />
      <span>{label || skillName}</span>
      {level !== undefined && (
        <span className="text-xs opacity-75 ml-1">
          {Math.round(level)}%
        </span>
      )}
    </>
  );
  
  const baseClasses = cn(
    'inline-flex items-center rounded-md border text-xs font-medium transition-colors duration-200',
    variantClasses[variant],
    badgeSizeClass,
    onClick && 'cursor-pointer hover:opacity-80',
    className
  );
  
  if (onClick) {
    return (
      <button className={baseClasses} onClick={onClick} type="button">
        {badgeContent}
      </button>
    );
  }
  
  return (
    <span className={baseClasses}>
      {badgeContent}
    </span>
  );
};
